<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  evaluationSampleList,
  evaluationSampleCreate,
  evaluationSampleDelete,
} from "@/common/api/rest/evaluation/sample";

import type { EvaluationType } from "@/common/types/evaluation";
import { SampleType } from "@/common/types/sample";
import { EvaluationSampleType } from "@/common/types/evaluation/sample";

import { searchSample } from "@/components";
import { baseConfirm, baseModal, baseTable } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const tableHeader = computed(() => [
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
]);

const tableData = ref<EvaluationSampleType[]>([]);
const selected = ref<EvaluationSampleType | null>(null);
const selectedList = ref<SampleType[]>([]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  if (!props.data) return;

  evaluationSampleList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  showModalAdd.value = true;
  selectedList.value = [];
}

function addCancel() {
  showModalAdd.value = false;
  selectedList.value = [];
}

function addSelect(data: SampleType[]) {
  selectedList.value = data;
}

async function addConfirm() {
  if (!props.data || !selectedList.value.length) {
    return;
  }

  showModalAdd.value = false;

  const promises = selectedList.value.map((sl) =>
    evaluationSampleCreate(props.data.account_id, {
      evaluation_id: props.data.evaluation_id,
      sample_id: sl.sample_id,
    }),
  );

  await Promise.all(promises)
    .then((responses) => {
      responses.forEach((response) => {
        const { success, message } = response;
        if (success) {
          toast({ message: message, type: "success" });
          update();
          fetchData();
        } else {
          toast({ message: message, type: "error" });
        }
      });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function del(data: EvaluationSampleType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function delCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function delConfirm() {
  if (!selected.value || !props.data) return;

  showConfirmDel.value = false;

  evaluationSampleDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        update();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.samples") }}
      </div>
    </template>

    <div class="content">
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :add="isEdit"
        :del="isEdit"
        @change:page="changePage($event)"
        @searching="searchItem($event)"
        @btn:add="add()"
        @btn:del="del($event)"
      />
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.sample'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-sample
          status="AVAILABLE"
          :account-id="data?.account_id"
          :ids="tableData.map((d) => d.sample_id)"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showConfirmDel"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.sample'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.code,
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }
}
</style>
